import { useEffect } from 'react';
import { useNavigationType } from 'react-router-dom';

// Kept outside React so every presentation keeps its own entry across remounts
const positionHistory: Record<string, any> = {};

/**
 * Store a view position (scroll offset or pan/zoom state) for a presentation.
 * @param id Presentation id
 * @param position Any serializable position object
 */
export const savePosition = (id: string | number | undefined, position: any) => {
  positionHistory[String(id ?? "root")] = position;
};

/**
 * Get a previously stored view position for a presentation.
 * @param id Presentation id
 * @returns Stored position, or undefined
 */
export const getSavedPosition = (id: string | number | undefined) => positionHistory[String(id ?? "root")];

/**
 * Remember the scroll position of elementRef and restore it on back navigation.
 * Items load asynchronously, so restoring retries until the element is large enough to scroll there.
 * @param data Presentation document
 * @param elementRef Scrollable element
 * @param scrollPosition Ref kept up to date by the view's onScroll handler
 * @returns Whether the view was reached via back navigation
 */
const useRememberScrollPosition = (data, elementRef, scrollPosition) => {
  const navigationType = useNavigationType();
  const isBackNavigation = navigationType === 'POP';
  const id = data?.id;

  // Store scroll position when the view unmounts
  useEffect(() => {
    if (!id) { return; }
    return () => {
      savePosition(id, { x: scrollPosition?.current?.x || 0, y: scrollPosition?.current?.y || 0 });
    };
  }, [id, scrollPosition]);

  // Scroll to previous position if back button was used to navigate here
  useEffect(() => {
    const saved = getSavedPosition(id);
    if (!id || !isBackNavigation || !saved) { return; }

    let frame = 0;
    let cancelled = false;
    const startedAt = Date.now();
    let element: HTMLElement | null = null;
    // Stop fighting the user if they start scrolling before content has finished loading
    const cancel = () => { cancelled = true; };

    const restore = () => {
      const el = elementRef.current;
      if (cancelled) { return; }
      if (el) {
        if (!element) {
          element = el;
          el.addEventListener("pointerdown", cancel, { once: true });
        }
        el.scrollLeft = saved.x;
        el.scrollTop = saved.y;
        const reached = Math.abs(el.scrollLeft - saved.x) <= 1 && Math.abs(el.scrollTop - saved.y) <= 1;
        if (reached) { return; }
      }
      if (Date.now() - startedAt < 3000) {
        frame = requestAnimationFrame(restore);
      }
    };
    frame = requestAnimationFrame(restore);

    return () => {
      cancelAnimationFrame(frame);
      element?.removeEventListener("pointerdown", cancel);
    };
  }, [id, isBackNavigation, elementRef]);

  return isBackNavigation;
};

export default useRememberScrollPosition;
